





































































































































































































































































































































































































































import { Component, Prop, Watch } from "vue-property-decorator";

import * as screenfull from "screenfull";

import { fmtDegLat, fmtDegLon, fmtHours } from "@wwtelescope/astro";
import { Folder, FolderUp, Place, Imageset, ImageSetLayer } from "@wwtelescope/engine";
import { ImageSetType } from "@wwtelescope/engine-types";
import {
  SetupForImagesetOptions,
  WWTAwareComponent,
} from "@wwtelescope/engine-vuex";
import { CreditMode, EmbedSettings } from "@wwtelescope/embed-common";
import { Meta } from "@sophosoft/vue-meta-decorator";
import { applyImageSetLayerSetting } from "@wwtelescope/engine-helpers";

/** The overall state of the WWT embed component. */
enum ComponentState {
  /** Waiting for resources and/or content to load. Not yet ready to start. */
  LoadingResources,

  /** Resources have loaded. We can start when the user activates us. */
  ReadyToStart,

  /** The user has activated us. */
  Started,
}

type ToolType = "crossfade" | "choose-background" | "playback-controls" | null;
type BottomSheetType = "text" | "video" | null;

class BackgroundImageset {
  public imagesetName: string;
  public displayName: string;

  constructor(displayName: string, imagesetName: string) {
    this.displayName = displayName;
    this.imagesetName = imagesetName;
  }
}

const skyBackgroundImagesets: BackgroundImageset[] = [
  new BackgroundImageset(
    "Optical (Terapixel DSS)",
    "Digitized Sky Survey (Color)"
  ),
  new BackgroundImageset(
    "Low-frequency radio (VLSS)",
    "VLSS: VLA Low-frequency Sky Survey (Radio)"
  ),
  new BackgroundImageset("Infrared (2MASS)", "2Mass: Imagery (Infrared)"),
  new BackgroundImageset("Infrared (SFD dust map)", "SFD Dust Map (Infrared)"),
  new BackgroundImageset("Ultraviolet (GALEX)", "GALEX (Ultraviolet)"),
  new BackgroundImageset(
    "X-Ray (ROSAT RASS)",
    "RASS: ROSAT All Sky Survey (X-ray)"
  ),
  new BackgroundImageset(
    "Gamma Rays (FERMI LAT 8-year)",
    "Fermi LAT 8-year (gamma)"
  ),
];

type Shape = { width: number; height: number };
const defaultWindowShape: Shape = { width: 1200, height: 900 };

type WwtComponentLayout = { top: string; height: string };

export type FolderItem = Folder | FolderUp | Imageset | Place;

@Component
export default class Embed extends WWTAwareComponent {
  CreditMode = CreditMode;

  @Prop({ default: new EmbedSettings() })
  readonly embedSettings!: EmbedSettings;
  @Prop({ default: "" }) jwstWtmlUrl!: string;
  @Prop({ default: "" }) url!: string;
  @Prop({ default: "" }) thumbnailUrl!: string;
  @Prop({ default: "" }) bgWtml!: string;
  @Prop({ default: "" }) bgName!: string;
  @Prop({ default: "" }) hubbleWtmlUrl!: string;

  componentState = ComponentState.LoadingResources;
  backgroundImagesets: BackgroundImageset[] = [];
  currentTool: ToolType = "crossfade";
  fullscreenModeActive = false;
  tourPlaybackJustEnded = false;
  windowShape = defaultWindowShape;

  collectionFolder: Folder | null = null;
  title = "Compare JWST and Hubble images of Carina!";
  description = "Pan, zoom, and compare the images using AAS WorldWide Telescope.";
  hashtags = ["jwst", "hubble", "wwt", "carina", "unfoldtheuniverse"];

  bottomSheet: BottomSheetType = null;
  hubbleLayer: ImageSetLayer | null = null;
  jwstLayer: ImageSetLayer | null = null;
  
  cfOpacity: number = 50;
  tab: number = 0;
  showSplashScreen = true;
  showLayers = true;
  touchscreen: boolean = false;
  showTooltipText = false;
  showTooltipVideo = false;
  showTooltipReset = false;

  get hashtagString() {
    return this.hashtags.join(",");
  }

  networks = [
    { name: "facebook", color: "#1877f2", text: "Share" },
    { name: "twitter", color: "#1da1f2", text: "Tweet" },
  ];

  // @Meta
  // getMetaInfo() {
  //   return {
  //     title: this.title,
  //     meta: [
  //       { property: "og:type", content: "website" },
  //       { property: "og:url", content: this.url },
  //       { property: "og:title", content: this.title },
  //       { property: "og:site_name", content: "WorldWide Telescope" },
  //       { property: "og:description", content: this.description },
  //       { property: "og:image", content: this.thumbnailUrl.replace("https", "http") },
  //       { property: "og:image:secure_url", content: this.thumbnailUrl },
  //       { property: "og:image:type", content: "image/jpeg" },
  //       { property: "og:image:width", content: "96" },
  //       { property: "og:image:height", content: "45" }
  //     ],
  //   }
  // }

  get smallSize() {
    // @ts-ignore
    return this.$vuetify.breakpoint.smAndDown;
  }

  get mobile() {
    return this.smallSize && this.touchscreen;
  }

  get imageLocation() {
    return require(`./assets/Carina Nebula Splash Screen${this.mobile ? ' Mobile' : ''} Close.png`);
  }

  get showFolderView() {
    const children = this.collectionFolder?.get_children();
    return children !== null && children !== undefined && children.length > 1;
  }

  get isLoadingState() {
    return this.componentState == ComponentState.LoadingResources || !this.layersLoaded;
  }

  get isReadyToStartState() {
    return this.componentState == ComponentState.ReadyToStart && this.layersLoaded;
  }

  get coordText() {
    if (this.wwtRenderType == ImageSetType.sky) {
      return `${fmtHours(this.wwtRARad)} ${fmtDegLat(this.wwtDecRad)}`;
    }

    return `${fmtDegLon(this.wwtRARad)} ${fmtDegLat(this.wwtDecRad)}`;
  }

  get curBackgroundImagesetName() {
    if (this.wwtBackgroundImageset == null) return "";
    return this.wwtBackgroundImageset.get_name();
  }

  set curBackgroundImagesetName(name: string) {
    this.setBackgroundImageByName(name);
  }

  get crossfadeOpacity() {
    return this.cfOpacity;
  }

  set crossfadeOpacity(o: number) {
    if (this.hubbleLayer) {
      applyImageSetLayerSetting(this.hubbleLayer, ["opacity", 1 - 0.01 * o]);
    }
    if (this.jwstLayer) {
      applyImageSetLayerSetting(this.jwstLayer, ["opacity", 0.01 * o]);
    }
    this.cfOpacity = o;
  }

  get fullscreenAvailable() {
    return screenfull.isEnabled;
  }

  get showBackgroundChooser() {
    if (this.wwtIsTourPlaying) return false;

    // TODO: we should wire in choices for other modes!
    return this.wwtRenderType == ImageSetType.sky;
  }

  get showCrossfader() {
    if (this.wwtIsTourPlaying) return false; // maybe show this if tour player is active but not playing?

    if (
      this.wwtForegroundImageset == null ||
      this.wwtForegroundImageset === undefined
    )
      return false;

    return this.wwtForegroundImageset != this.wwtBackgroundImageset;
  }

  get showPlaybackControls() {
    return this.wwtIsTourPlayerActive && !this.wwtIsTourPlaying;
  }

  get showToolMenu() {
    // This should return true if there are any tools to show.
    return (
      this.showBackgroundChooser ||
      this.showCrossfader ||
      this.showPlaybackControls
    );
  }

  async created() {
    this.touchscreen = matchMedia('(hover: none)').matches;

    let prom = this.waitForReady().then(() => {
      for (const s of this.embedSettings.asSettings()) {
        this.applySetting(s);
      }
    });

    if (this.embedSettings.tourUrl.length) {
      prom = prom.then(async () => {
        // TODO: figure out a good thing to do here
        this.backgroundImagesets = [...skyBackgroundImagesets];

        await this.loadTour({
          url: this.embedSettings.tourUrl,
          play: false,
        });

        this.componentState = ComponentState.ReadyToStart;
        this.setTourPlayerLeaveSettingsWhenStopped(true);
        this.currentTool = "playback-controls";
      });
    } else {
      // Many more possibilities if we're not playing a tour ...
      if (this.embedSettings.wtmlUrl.length) {
        prom = prom.then(async () => {
          const folder = await this.loadImageCollection({
            url: this.embedSettings.wtmlUrl,
            loadChildFolders: false,
          });

          if (this.embedSettings.wtmlPlace) {
            // Currently, there is an issue with the `places` field of a `Folder`
            // object populating correctly. Thus, we iterate over `children` instead
            for (const pl of folder.get_children() ?? []) {
              if (!(pl instanceof Place)) {
                continue;
              }
              if (pl.get_name() == this.embedSettings.wtmlPlace) {
                /* This is nominally an async Action, but with `instant: true` it's ... instant */
                this.gotoTarget({
                  place: pl,
                  noZoom: false,
                  instant: true,
                  trackObject: true,
                });
              }
            }
          }
        });
      }

      prom.then(() => {
        // setupForImageset() will apply a default background that is appropriate
        // for the foreground, but we want to be able to override it.

        let backgroundWasInitialized = false;
        let bgName = this.embedSettings.backgroundImagesetName;

        if (this.embedSettings.foregroundImagesetName.length) {
          const img = this.lookupImageset(
            this.embedSettings.foregroundImagesetName
          );

          if (img !== null) {
            // If the imageset is a panorama, we want to set it to be the background
            const isPanorama = img.get_dataSetType() == ImageSetType.panorama;
            const options: SetupForImagesetOptions = { foreground: img };
            if (isPanorama) {
              options.background = img;
              backgroundWasInitialized = true;
            }

            // For setup of planetary modes to work, we need to pass the specified
            // background imageset to setupForImageset().
            // For a panorama, we've already set the imageset itself as the background
            if (!isPanorama && bgName.length) {
              const bkg = this.lookupImageset(bgName);
              if (bkg !== null) {
                options.background = bkg;
                backgroundWasInitialized = true;
              }
            }

            this.setupForImageset(options);
          }
        }

        this.loadImageCollection({
          url: this.bgWtml,
          loadChildFolders: true,
        }).then((_folder) => {
          this.curBackgroundImagesetName = this.bgName;
          this.backgroundImagesets.unshift(
            new BackgroundImageset("unWISE", "unwise")
          );
        });

        this.loadImageCollection({
          url: this.hubbleWtmlUrl,
          loadChildFolders: false
        }).then((folder) => {
          const children = folder.get_children();
          if (children == null) { return; }
          const item = children[0] as Place;
          const imageset = item.get_backgroundImageset() ?? item.get_studyImageset();
          if (imageset === null) { return; }
          this.addImageSetLayer({
            url: imageset.get_url(),
            mode: "autodetect",
            name: "Hubble Carina",
            goto: false
          }).then((layer) => {
            this.hubbleLayer = layer;
            applyImageSetLayerSetting(layer , ["opacity", 0.5]);
          });
        });

        this.loadImageCollection({
          url: this.jwstWtmlUrl,
          loadChildFolders: false
        }).then((folder) => {
          const children = folder.get_children();
          if (children == null) { return; }
          const item = children[0] as Place;
          const imageset = item.get_backgroundImageset() ?? item.get_studyImageset();
          if (imageset === null) { return; }
          this.setForegroundOpacity(0);
          this.addImageSetLayer({
            url: imageset.get_url(),
            mode: "autodetect",
            name: "JWST Carina",
            goto: false
          }).then((layer) => {
            this.jwstLayer = layer;
            this.resetView();
            applyImageSetLayerSetting(layer , ["opacity", 0.5]);
          });
        });

        if (!backgroundWasInitialized) {
          if (!bgName.length) {
            // Empty bgname implies that we should choose a default background. If
            // setupForImageset() didn't do that for us, go with:
            bgName = "Digitized Sky Survey (Color)";
          }

          this.setBackgroundImageByName(bgName);
        }

        // TODO: DTRT in different modes.
        this.backgroundImagesets = [...skyBackgroundImagesets];
        let foundBG = false;

        for (const bgi of this.backgroundImagesets) {
          if (bgi.imagesetName == bgName) {
            foundBG = true;
            break;
          }
        }

        if (!foundBG) {
          this.backgroundImagesets.unshift(
            new BackgroundImageset(bgName, bgName)
          );
        }

        this.componentState = ComponentState.Started;
      });
    }
  }

  mounted() {
    if (screenfull.isEnabled) {
      screenfull.on("change", this.onFullscreenEvent);
    }

    window.addEventListener("resize", this.onResizeEvent);
    // ResizeObserver not yet in TypeScript but we should start using it when
    // available. If we're in an iframe, our shape might change spontaneously.
    // const ro = new ResizeObserver(entries => this.onResizeEvent());
    // ro.observer(this.$el);
    this.onResizeEvent();
  }

  destroyed() {
    if (screenfull.isEnabled) {
      screenfull.off("change", this.onFullscreenEvent);
    }

    window.removeEventListener("resize", this.onResizeEvent);
  }

  selectTool(name: ToolType) {
    if (this.currentTool == name) {
      this.currentTool = null;
    } else {
      this.currentTool = name;
    }
  }

  selectBottomSheet(name: BottomSheetType) {
    if (this.bottomSheet == name) {
      this.bottomSheet = null;
      this.$nextTick(() => {
        this.blurActiveElement();
      });
    } else {
      this.bottomSheet = name;
    }
  }

  get layersLoaded() {
    return this.hubbleLayer != null && this.jwstLayer != null;
  }

  get showVideoSheet() {
    return this.bottomSheet === 'video';
  }
  set showVideoSheet(value: boolean) {
    this.selectBottomSheet('video');
    if (!value) {
      const video = document.querySelector("#info-video") as HTMLVideoElement;
      video.pause();
    }
  }

  get showTextSheet() {
    return this.bottomSheet === 'text';
  }
  set showTextSheet(_value) {
    this.selectBottomSheet('text');
  }

  blurActiveElement() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
  }


  doZoom(zoomIn: boolean) {
    if (zoomIn) {
      this.zoom(1 / 1.3);
    } else {
      this.zoom(1.3);
    }
  }

  toggleFullscreen() {
    if (screenfull.isEnabled) {
      screenfull.toggle();
    }
  }

  onFullscreenEvent() {
    // NB: we need the isEnabled check to make TypeScript happy even though it
    // is not necessary in practice here.
    if (screenfull.isEnabled) {
      this.fullscreenModeActive = screenfull.isFullscreen;
    }
  }

  onResizeEvent() {
    const width = this.$el.clientWidth;
    const height = this.$el.clientHeight;

    if (width > 0 && height > 0) {
      this.windowShape = { width, height };
    } else {
      this.windowShape = defaultWindowShape;
    }
  }

  startInteractive() {
    this.componentState = ComponentState.Started;

    if (this.embedSettings.tourUrl.length) {
      this.startTour();
    }
  }

  get tourPlaybackIcon() {
    if (this.tourPlaybackJustEnded) {
      return "undo-alt";
    }

    if (this.wwtIsTourPlaying) {
      return "pause";
    }

    return "play";
  }

  resetView(instant: boolean = true) {
    const D2R = Math.PI / 180.0;
    if (this.jwstLayer === null) { return; }
    const imageset = this.jwstLayer.get_imageSet();
    this.gotoRADecZoom({
      raRad: D2R * imageset.get_centerX(),
      decRad: D2R * imageset.get_centerY(),
      zoomDeg: 0.8595,
      rollRad: 1.799,
      instant: instant,
    });
  }

  closeSplashScreenIfLoaded() {
    if (this.componentState == ComponentState.Started) {
      this.showSplashScreen = false;
    }
  }

  tourPlaybackButtonClicked() {
    if (this.wwtIsTourPlayerActive) {
      // If we're playing and our window is tall, we have styling active that
      // keeps the WWT widget in a widescreen-ish format to preserve tour
      // layout. If we're stopping playback, this styling will go away. Since
      // the WWT widget tracks its view height as an angular size, we need to
      // tweak it in order to preserve continuity when the tour is paused. This
      // isn't 100% necessary but is cool when it works. It doesn't work if the
      // pause occurs in a very zoomed-out state, where we can't zoom out any
      // farther because we hit the zoom clamps.
      //
      // Keep this in sync with wwtComponentLayout.
      //
      // TODO: make sure this works in 3D mode.
      let newView = null;

      if (this.wwtIsTourPlaying && this.wwtComponentLayout.top != "0") {
        const curHeight = this.windowShape.width * 0.75;
        newView = {
          raRad: 1.0 * this.wwtRARad,
          decRad: 1.0 * this.wwtDecRad,
          zoomDeg: (this.wwtZoomDeg * this.windowShape.height) / curHeight,
          instant: true,
        };
      }

      if (this.tourPlaybackJustEnded) {
        // Restart from beginning. (seekToTourTimecode() starts playback.)
        this.seekToTourTimecode(0);
        this.tourPlaybackJustEnded = false;
      } else {
        this.toggleTourPlayPauseState();
      }

      if (newView !== null) {
        this.gotoRADecZoom(newView);
      }
    }
  }

  formatTimecode(seconds: number): string {
    if (seconds < 0) return "-:--";

    const minutes = Math.floor(seconds / 60);
    seconds = Math.round(seconds - 60 * minutes);
    return minutes.toString() + ":" + seconds.toString().padStart(2, "0");
  }

  get twoWayTourTimecode() {
    return this.wwtTourTimecode;
  }

  set twoWayTourTimecode(code: number) {
    this.seekToTourTimecode(code);
    this.tourPlaybackJustEnded = false;
  }

  @Watch("wwtTourCompletions")
  onTourCompletionsChanged(_count: number) {
    this.tourPlaybackJustEnded = true;
  }

  @Watch("componentState")
  onComponentStateChanged(state: ComponentState) {
    if (state === ComponentState.Started) {
      this.showSplashScreen = true;
    }
  }

  @Watch("showLayers")
  onShowLayersChanged(show: boolean) {
    if (this.hubbleLayer) {
      applyImageSetLayerSetting(this.hubbleLayer, ["enabled", show]);
    }
    if (this.jwstLayer) {
      applyImageSetLayerSetting(this.jwstLayer, ["enabled", show]);
    }
  }

  // This property is used to achieve a widescreen effect when playing tours
  // back on a portrait-mode screen, such as on a mobile device. Tours have to
  // be designed with a target screen aspect ratio in mind, so without the
  // widescreen effect the content will get cut off.
  //
  // Keep this in sync with toggleTourPlayback.
  get wwtComponentLayout(): WwtComponentLayout {
    if (this.wwtIsTourPlaying) {
      if (this.windowShape.height > this.windowShape.width) {
        const wwtHeight = this.windowShape.width * 0.75; // => 4:3 aspect ratio
        const height = wwtHeight + "px";
        const top = 0.5 * (this.windowShape.height - wwtHeight) + "px";
        return { top, height };
      }
    }
    return { top: "0", height: "100%" };
  }
}
